import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook para buscar formulários atribuídos a uma entidade
 */
export function useFormAssignments(
  entityType: "product" | "client" | "supplier" | "transaction" | "custom",
  entityId?: string | null
) {
  return useQuery({
    queryKey: ["form-assignments", entityType, entityId],
    queryFn: async () => {
      let query = supabase
        .from("form_assignments")
        .select("*, forms(*)")
        .eq("entity_type", entityType);

      if (entityId) {
        query = query.eq("entity_id", entityId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data || [];
    },
    enabled: !!entityType,
  });
}

/**
 * Hook para buscar um formulário completo com campos e opções
 */
export function useFormWithFields(
  formId: string,
  options?: { enabled?: boolean; refetchOnMount?: boolean | "always"; staleTime?: number }
) {
  return useQuery({
    queryKey: ["form-with-fields", formId],
    enabled: options?.enabled !== false && !!formId,
    staleTime: options?.staleTime ?? 5 * 60 * 1000, // 5 minutos padrão
    gcTime: 10 * 60 * 1000, // 10 minutos - cache mantido por 10 minutos
    refetchOnMount: options?.refetchOnMount ?? false,
    queryFn: async () => {
      // Buscar formulário
      const { data: form, error: formError } = await supabase
        .from("forms")
        .select("*")
        .eq("id", formId)
        .single();

      if (formError) throw formError;

      // Buscar campos
      const { data: fields, error: fieldsError } = await supabase
        .from("form_fields")
        .select("*")
        .eq("form_id", formId)
        .order("ordem", { ascending: true });

      if (fieldsError) throw fieldsError;

      // Buscar opções apenas para campos que têm opções (select, multiselect, radio, checkbox)
      const fieldsWithOptions = await Promise.all(
        (fields || []).map(async (field) => {
          // Apenas buscar opções para campos que têm opções
          if (["select", "multiselect", "radio", "checkbox"].includes(field.tipo)) {
            const { data: options } = await supabase
              .from("form_field_options")
              .select("*")
              .eq("field_id", field.id)
              .order("ordem", { ascending: true });

            // Organizar opções hierarquicamente
            const optionsList = options || [];
            const rootOptions = optionsList.filter((opt: any) => !opt.parent_option_id);
            const buildTree = (parentId: string | null): any[] => {
              return optionsList
                .filter((opt: any) => opt.parent_option_id === parentId)
                .map((opt: any) => ({
                  ...opt,
                  sub_opcoes: buildTree(opt.id),
                }));
            };

            return {
              ...field,
              options: rootOptions.map((opt: any) => ({
                ...opt,
                sub_opcoes: buildTree(opt.id),
              })),
            };
          } else {
            // Para campos de texto e outros tipos, não buscar opções
            return {
              ...field,
              options: [],
            };
          }
        })
      );

      // Buscar dependências apenas para campos que podem ter dependências
      // (select, multiselect, radio) - campos de texto não têm dependências
      const fieldsThatCanHaveDependencies = fieldsWithOptions.filter(
        (f: any) => ["select", "multiselect", "radio"].includes(f.tipo)
      );
      
      let dependencies: any[] = [];
      if (fieldsThatCanHaveDependencies.length > 0) {
        const { data: depsData } = await supabase
          .from("form_field_dependencies")
          .select("*")
          .in(
            "field_id",
            fieldsThatCanHaveDependencies.map((f: any) => f.id)
          );
        dependencies = depsData || [];
      }

      return {
        form,
        fields: fieldsWithOptions,
        dependencies: dependencies || [],
      };
    },
  });
}

